import { COMPETENCIES } from '../lib/constants';

export default function Dashboard({ entries, setView, isMobile }) {
  const total     = entries.length;
  const difficult = entries.filter(e => e.is_difficult).length;
  const approved  = entries.filter(e => ['approved','approved_with_comments'].includes(e.approval_status)).length;
  const pending   = entries.filter(e => e.approval_status === 'pending').length;
  const avg       = total ? (entries.reduce((a, e) => a + e.milestone_level, 0) / total).toFixed(1) : '—';
  const compCounts = {};
  COMPETENCIES.forEach(c => { compCounts[c.id] = entries.filter(e => (e.competency_ids || []).includes(c.id)).length; });
  const covered   = Object.values(compCounts).filter(v => v > 0).length;
  const recent    = entries.slice(0, 5);

  if (isMobile) {
    return (
      <div style={{ padding: '16px', paddingBottom: 80 }}>
        {/* Quick action buttons */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <button onClick={() => setView('new')}
            style={{ flex: 1, padding: '16px', background: '#1A3A5C', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <span style={{ fontSize: 18 }}>＋</span> Log Case
          </button>
          <button onClick={() => setView('activity')}
            style={{ flex: 1, padding: '16px', background: '#2D6A8A', color: '#fff', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <span style={{ fontSize: 18 }}>📋</span> Add Activity
          </button>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total Cases', val: total, color: '#1A3A5C' },
            { label: 'Avg Level',   val: avg,   color: '#2D6A8A' },
            { label: 'Approved',    val: approved, color: '#1E5C40' },
            { label: 'Pending',     val: pending,  color: '#C4622A' },
          ].map((m, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ fontFamily: "'DM Serif Display'", fontSize: 28, color: m.color }}>{m.val}</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{m.label}</div>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '16px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <span style={{ fontFamily: "'DM Serif Display'", fontSize: 16, color: '#1A1A1F' }}>Competency Progress</span>
            <span style={{ fontSize: 12, color: '#888' }}>{covered}/{COMPETENCIES.length} areas</span>
          </div>
          {COMPETENCIES.map(c => {
            const count = compCounts[c.id];
            const pct = Math.min(100, Math.round((count / c.min) * 100));
            return (
              <div key={c.id} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: '#555' }}>{c.label}</span>
                  <span style={{ fontSize: 11, color: '#999' }}>{count}/{c.min}</span>
                </div>
                <div style={{ height: 5, background: '#EEE', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 10, width: `${pct}%`, background: pct >= 100 ? '#1E5C40' : c.color }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent entries */}
        <div style={{ fontFamily: "'DM Serif Display'", fontSize: 16, color: '#1A1A1F', marginBottom: 12 }}>Recent Cases</div>
        {recent.length === 0
          ? <div style={{ textAlign: 'center', padding: '30px 0', color: '#aaa', fontSize: 14 }}>No cases yet — log your first case!</div>
          : recent.map(e => {
              const isPending  = e.approval_status === 'pending';
              const isApproved = ['approved','approved_with_comments'].includes(e.approval_status);
              const isRejected = e.approval_status === 'rejected';
              return (
                <div key={e.id} style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `3px solid ${isApproved ? '#1E5C40' : isRejected ? '#8A2D2D' : '#C4A97C'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: '#999' }}>{e.date}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: isApproved ? '#1E5C40' : isRejected ? '#8A2D2D' : '#C4622A' }}>
                      {isApproved ? '✓ Approved' : isRejected ? '✗ Rejected' : '⏳ Pending'}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{(e.case_summary || '').slice(0, 80)}{(e.case_summary || '').length > 80 ? '…' : ''}</div>
                </div>
              );
            })}

        {/* Motivational sentence */}
        <div style={{
          margin: '24px 0 8px',
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #1A3A5C, #2D6A8A)',
          borderRadius: 12,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', lineHeight: 1.8, fontStyle: 'italic', fontFamily: "'DM Serif Display'" }}>
            Every case you log is a testament to your dedication and compassion.
          </div>
          <div style={{ fontSize: 12, color: 'rgba(126,184,212,0.9)', marginTop: 6, lineHeight: 1.6 }}>
            Let's capture your journey and celebrate your growth.
          </div>
        </div>

      </div>
    );
  }

  // Desktop view
  return (
    <div style={s.content}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Dashboard</h1>
          <p style={s.sub}>Your training overview</p>
        </div>
        <button style={s.primaryBtn} onClick={() => setView('new')}>+ Log New Case</button>
      </div>
      <div style={s.metrics}>
        {[
          { label: 'Cases Logged',   val: total,   sub: 'total entries' },
          { label: 'Avg Milestone',  val: avg,     sub: 'out of 5.0' },
          { label: 'Approved',       val: approved, sub: 'cases approved' },
          { label: 'Areas Covered',  val: `${covered}/${COMPETENCIES.length}`, sub: 'competencies' },
        ].map((m, i) => (
          <div key={i} style={s.metricCard}>
            <div style={s.metricVal}>{m.val}</div>
            <div style={s.metricLabel}>{m.label}</div>
            <div style={s.metricSub}>{m.sub}</div>
          </div>
        ))}
      </div>
      <div style={s.grid}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Competency Progress</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginTop: 16 }}>
            {COMPETENCIES.map(c => {
              const count = compCounts[c.id];
              const pct = Math.min(100, Math.round((count / c.min) * 100));
              return (
                <div key={c.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: '#555', fontWeight: 500 }}>{c.label}</span>
                    <span style={{ fontSize: 12, color: '#999' }}>{count}/{c.min}</span>
                  </div>
                  <div style={{ height: 5, background: '#EEE', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 10, width: `${pct}%`, background: pct >= 100 ? '#1E5C40' : c.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Recent Entries</h3>
          {recent.length === 0
            ? <div style={{ fontSize: 13, color: '#aaa', textAlign: 'center', padding: '40px 0' }}>No cases yet!</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
                {recent.map(e => (
                  <div key={e.id} style={{ padding: '10px 12px', borderRadius: 8, background: '#FAF9F7', borderLeft: '3px solid #DDD' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: '#999' }}>{e.date}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, color: ['approved','approved_with_comments'].includes(e.approval_status) ? '#1E5C40' : e.approval_status === 'rejected' ? '#8A2D2D' : '#C4622A' }}>
                        {['approved','approved_with_comments'].includes(e.approval_status) ? '✓ Approved' : e.approval_status === 'rejected' ? '✗ Rejected' : '⏳ Pending'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#333' }}>{(e.case_summary || '').slice(0, 90)}…</div>
                  </div>
                ))}
              </div>}
        </div>
      </div>
    </div>
  );
}

const s = {
  content: { padding: '36px 40px', maxWidth: 1100, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontFamily: "'DM Serif Display'", fontSize: 30, color: '#1A1A1F', marginBottom: 4 },
  sub: { fontSize: 13, color: '#888' },
  primaryBtn: { background: '#1A3A5C', color: '#F5F0E8', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'" },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 },
  metricCard: { background: '#fff', borderRadius: 12, padding: '20px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  metricVal: { fontFamily: "'DM Serif Display'", fontSize: 34, color: '#1A1A1F', marginBottom: 4 },
  metricLabel: { fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 2 },
  metricSub: { fontSize: 11, color: '#999' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  card: { background: '#fff', borderRadius: 12, padding: '22px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  cardTitle: { fontFamily: "'DM Serif Display'", fontSize: 17, color: '#1A1A1F' },
};
